/**
 * The sign-in state machine, as Accounts_SSO_Prototype.html implements it end to end.
 *
 * A pure reducer: `(state, event) => state`. No HTTP, no Angular, no storage. The store in
 * core/stores wires it to the API; every rule about what may follow what lives here, where it can be
 * tested exhaustively without a browser.
 *
 * WHY A REDUCER AND NOT FLAGS ON A COMPONENT
 *
 * The flow has nine states, four of them terminal, and two pairs that look alike and must not be
 * merged. Expressed as booleans on a page component that is exactly the shape of bug the invariants
 * warn about: a `no_access` screen that silently behaves like `no_merchant_account`, or an
 * `otp_expired` that offers the `otp_attempts_exhausted` exit. As a reducer, "which transitions
 * exist" is a table a test can read.
 */
import type {
  EligibleAccount,
  ApplicationSummary,
  OtpChallengeView,
  OtpChannel,
  SignInState,
} from '../models/sso.models';

export interface SignInContext {
  readonly state: SignInState;
  /**
   * FOUR HANDLES, NOT ONE. The service issues a different short-lived handle at each step, and each
   * is scoped to that step -- see the header of core/models/sso.models.ts. An earlier version of
   * this file carried a single `flowId`, which does not exist in the contract.
   */
  readonly req: string | null;
  readonly identityRef: string | null;
  readonly mfaToken: string | null;
  readonly selectionToken: string | null;
  readonly application: ApplicationSummary | null;
  /** What the user typed. Held to render "signing in as …", never re-sent as an identifier. */
  readonly identifier: string | null;
  /**
   * The channels the SERVICE says this identity can receive a code on, and the masks it disclosed.
   *
   * Both come from the service and neither is derived here. `/identify` names ONE channel before
   * the password is verified -- it prefers email and masks the other as null, so as not to disclose
   * which contacts exist to someone who has not authenticated. `/password` then returns the full
   * list, because at that point the password HAS been verified. So `channels` is replaced, not
   * merged, when the second answer arrives.
   *
   * The masks are only ever as complete as `/identify` made them: after the password the service
   * returns the channel list without masks, so the picker labels a channel it cannot mask by name
   * alone. That is a display limitation, not a correctness one.
   */
  readonly channels: readonly OtpChannel[];
  readonly maskedEmail: string | null;
  readonly maskedPhone: string | null;
  readonly otp: OtpChallengeView | null;
  readonly accounts: readonly EligibleAccount[];
  /**
   * What /select-account returned: the code, the caller's state echoed back, and the redirect_uri
   * the SERVICE validated. Null until the flow reaches its hand-off.
   */
  readonly handOff: {
    readonly code: string;
    readonly state: string;
    readonly redirectUri: string;
  } | null;
  /** The last error code from the service. Rendered through the catalogue, never raw. */
  readonly errorCode: string | null;
  readonly retryAfterSeconds: number | null;
  readonly busy: boolean;
}

export type SignInEvent =
  | { readonly type: 'SUBMIT' } // any step's request left the client
  | {
      readonly type: 'AUTHORIZE_ACCEPTED';
      readonly req: string;
      /** Null on the normal entry: only the handle knows which application this is. */
      readonly application: ApplicationSummary | null;
    }
  | {
      readonly type: 'IDENTIFIED';
      readonly identityRef: string;
      readonly identifier: string;
      readonly channels: readonly OtpChannel[];
      readonly maskedEmail?: string | null;
      readonly maskedPhone?: string | null;
    }
  /**
   * The password was right, a code is needed, and the user gets to say WHERE (GPGT-41335).
   *
   * Separate from `MFA_REQUIRED` because nothing has been sent yet. Sending first and offering a
   * change afterwards costs the user a message they did not ask for, on a channel they may not be
   * looking at -- and it spends one of their `max_sends_per_window` before they have chosen.
   */
  | {
      readonly type: 'CHANNELS_OFFERED';
      readonly mfaToken: string;
      readonly channels: readonly OtpChannel[];
    }
  /** Back to the picker from the code screen: the wrong channel was chosen, or nothing arrived. */
  | { readonly type: 'CHANNEL_RESELECTED' }
  | { readonly type: 'MFA_REQUIRED'; readonly mfaToken: string; readonly otp: OtpChallengeView }
  | { readonly type: 'OTP_REISSUED'; readonly otp: OtpChallengeView }
  | { readonly type: 'SELECTION_READY'; readonly selectionToken: string }
  | {
      readonly type: 'ACCOUNTS_LOADED';
      readonly accounts: readonly EligibleAccount[];
      /** The server's own verdict. Never re-derived from the list. */
      readonly skipSelection: boolean;
    }
  | {
      readonly type: 'AUTHORIZED';
      /** The three values /select-account returned. The SPA reconstructs none of them. */
      readonly handOff: {
        readonly code: string;
        readonly state: string;
        readonly redirectUri: string;
      };
    }
  | { readonly type: 'MUST_CHANGE_PASSWORD' }
  /**
   * `attemptsRemaining` rides along for the same reason `retryAfterSeconds` does (GPGT-41748).
   *
   * The service reports it on every OTP refusal and the store used to drop it, so a wrong code
   * decremented the budget on the server and nothing on this origin heard about it. Optional
   * because most failures have no budget attached — a network error carries neither field.
   */
  | {
      readonly type: 'FAILED';
      readonly code: string;
      readonly retryAfterSeconds?: number;
      readonly attemptsRemaining?: number;
    }
  | { readonly type: 'RESTART' };

export const INITIAL_CONTEXT: SignInContext = Object.freeze({
  state: 'identifier' as SignInState,
  req: null,
  identityRef: null,
  mfaToken: null,
  selectionToken: null,
  application: null,
  identifier: null,
  channels: [] as readonly OtpChannel[],
  maskedEmail: null,
  maskedPhone: null,
  otp: null,
  accounts: [] as readonly EligibleAccount[],
  handOff: null,
  errorCode: null,
  retryAfterSeconds: null,
  busy: false,
});

/** The four states from which nothing follows but a restart. */
export const TERMINAL_STATES: readonly SignInState[] = Object.freeze([
  'locked',
  'no_access',
  'no_merchant_account',
  'redirecting',
]);

export function isTerminal(state: SignInState): boolean {
  return TERMINAL_STATES.includes(state);
}

/**
 * Error codes that END the flow, and the screen each one lands on.
 *
 * Anything absent from this map is a recoverable error: it sets `errorCode` and leaves the state
 * alone, so the user retries on the screen they are already looking at. That default is why
 * `invalid_credentials` does not need an entry and `account_locked` does.
 */
const TERMINAL_FOR_CODE: Readonly<Record<string, SignInState>> = Object.freeze({
  account_locked: 'locked',
  otp_attempts_exhausted: 'locked',
  no_access_to_application: 'no_access',
  no_merchant_account: 'no_merchant_account',
});

export function reduce(context: SignInContext, event: SignInEvent): SignInContext {
  switch (event.type) {
    case 'SUBMIT':
      // Clearing the error on submit is deliberate: leaving the previous failure on screen while a
      // new request is in flight reads as the new attempt having already failed.
      return { ...context, busy: true, errorCode: null, retryAfterSeconds: null };

    case 'AUTHORIZE_ACCEPTED':
      // The `req` handle from /authorize. It binds every later step to one application, one
      // redirect_uri and one state, which is why it is threaded through identify, password and
      // select-account rather than kept only for the hand-off.
      return { ...context, req: event.req, application: event.application, busy: false };

    case 'IDENTIFIED':
      // Only from the identifier screen. A late response after the user went back must not drag
      // them forward again.
      if (context.state !== 'identifier') return context;
      return {
        ...context,
        state: 'password',
        busy: false,
        identityRef: event.identityRef,
        identifier: event.identifier,
        // Kept rather than dropped: an earlier version took `channels` on this event and ignored
        // it, so the one place that knew a mask could not use it.
        channels: event.channels,
        maskedEmail: event.maskedEmail ?? null,
        maskedPhone: event.maskedPhone ?? null,
      };

    case 'CHANNELS_OFFERED':
      // Only from the password screen. A late answer after the user went back must not drag them
      // forward, the rule IDENTIFIED already follows.
      if (context.state !== 'password') return context;
      return {
        ...context,
        state: 'channel_choice',
        busy: false,
        mfaToken: event.mfaToken,
        // REPLACED, not merged: this list is the authenticated one. /identify's was deliberately
        // partial.
        channels: event.channels,
      };

    case 'CHANNEL_RESELECTED':
      // From the code screen only, and it keeps the mfa_token: the handle is scoped to the MFA
      // step, not to one delivery, so choosing again does not restart the flow. The challenge view
      // is dropped so the screen cannot show a destination no code is coming to.
      if (context.state !== 'otp') return context;
      return { ...context, state: 'channel_choice', busy: false, otp: null, errorCode: null };

    case 'MFA_REQUIRED':
      // Reachable from BOTH: `channel_choice` when the user picked, and `password` when there was
      // nothing to pick -- one channel is not a choice, and stopping to confirm it would be a
      // screen that asks a question with one answer.
      if (context.state !== 'password' && context.state !== 'channel_choice') return context;
      return { ...context, state: 'otp', busy: false, mfaToken: event.mfaToken, otp: event.otp };

    case 'SELECTION_READY':
      // The trusted-device path (UC-06): the password step returned a `selection_token` instead of
      // an `mfa_token`, so the OTP step is skipped -- and only that step. Reachable from `password`
      // and from `otp`, because verifying a code also yields a selection token.
      if (context.state !== 'password' && context.state !== 'otp') return context;
      return { ...context, busy: false, selectionToken: event.selectionToken };

    case 'OTP_REISSUED':
      // A resend keeps the user on the OTP screen and replaces the challenge view. It is NOT a
      // transition, and it clears the previous error so the new code starts clean.
      if (context.state !== 'otp') return context;
      return { ...context, busy: false, otp: event.otp, errorCode: null };

    case 'ACCOUNTS_LOADED': {
      // Reachable from the password step (a trusted device skipped the OTP, UC-06) and from the OTP
      // step. Not from anywhere else.
      if (context.state !== 'password' && context.state !== 'otp') return context;

      /**
       * `skip_selection` COMES FROM THE SERVER. UC-02 -- "single eligible account skips the picker
       * with NO flash of the selection screen" -- is a property of this transition, so the picker is
       * never entered rather than entered and hidden.
       *
       * An earlier version filtered the list on an `eligible` boolean it had invented. The endpoint
       * has no such field: it returns the accounts that ARE eligible and answers
       * `no_merchant_account` / `no_access_to_application` when there are none. Re-deriving the
       * decision client-side is the "two implementations of eligibility" failure the specification
       * warns about, in miniature.
       */
      if (event.skipSelection && event.accounts.length > 0) {
        return { ...context, state: 'redirecting', busy: true, accounts: event.accounts };
      }

      if (event.accounts.length === 0) {
        // Zero is not an empty picker. WHICH terminal applies is the server's call, so the caller
        // dispatches FAILED with the code it was given rather than guessing here.
        return { ...context, busy: false, accounts: event.accounts };
      }

      return { ...context, state: 'account_picker', busy: false, accounts: event.accounts };
    }

    case 'AUTHORIZED':
      return {
        ...context,
        state: 'redirecting',
        // Stays busy: the next thing that happens is a navigation away from this origin, and an
        // idle-looking screen in between invites a second click.
        busy: true,
        handOff: event.handOff,
      };

    case 'MUST_CHANGE_PASSWORD':
      if (context.state !== 'password' && context.state !== 'otp') return context;
      return { ...context, state: 'must_change_password', busy: false };

    case 'FAILED': {
      const terminal = TERMINAL_FOR_CODE[event.code];
      return {
        ...context,
        state: terminal ?? context.state,
        busy: false,
        errorCode: event.code,
        retryAfterSeconds: event.retryAfterSeconds ?? null,
        /**
         * THE BUDGET LIVES ON THE CHALLENGE, not beside it (GPGT-41748).
         *
         * A second context field would be a second answer to "how many guesses are left", and the
         * two would drift the moment a resend replaced the challenge — `OTP_REISSUED` swaps `otp`
         * wholesale and would leave a stale sibling behind. Updating the challenge in place keeps
         * one home for the number, and a resend resets it for free.
         *
         * Both guards matter. With no challenge there is nothing to update, and an event with no
         * number must LEAVE the existing one alone rather than clear it: `otp_send_throttled`
         * carries no budget, and treating that as "unknown" would blank a counter the user is
         * looking at while their live challenge still has attempts on it.
         */
        otp:
          context.otp && event.attemptsRemaining !== undefined
            ? { ...context.otp, attempts_remaining: event.attemptsRemaining }
            : context.otp,
      };
    }

    case 'RESTART':
      // Everything goes, including the flow id. A restart that kept the flow id would resume a
      // server-side flow the user believes they abandoned.
      return INITIAL_CONTEXT;

    default: {
      // Exhaustiveness: adding an event without handling it fails to compile rather than silently
      // returning the previous state.
      const unreachable: never = event;
      return unreachable;
    }
  }
}
